// Owner: dashboards-core. The address of a dashboard (`/dashboards/<key>?…`) as the route reads it. A light
// module (types only from the dashboard schemas), so the route checks its address without loading the page.
import type { EvaluateDashboardInput } from '@dataroom/core/dashboards';

/** The parts of a dashboard's address that are its filters (`EvaluateDashboardInput`). */
export const FILTER_KEYS = [
  'period',
  'anchor',
  'owners',
  'teams',
  'pipeline',
  'timezone',
  'range',
  'values',
  'crossFilters',
] as const satisfies ReadonlyArray<keyof EvaluateDashboardInput>;
export type FilterKey = (typeof FILTER_KEYS)[number];
// Every filter of the dashboard input is a part of the address (a new one must be added above).
const everyFilterKey: Exclude<keyof EvaluateDashboardInput, FilterKey> extends never ? true : never = true;
void everyFilterKey;

/**
 * The address of a dashboard: `edit` opens the builder, `cf` carries the cross filters as a token, and the
 * filters are as the router parsed them — not yet checked. Checking them needs the dashboard schemas and
 * period arithmetic, which load with the page: `DashboardRoute` checks every part (`parseFilters`), applies
 * what is valid and drops the rest with a notice, so an old or edited link still opens the dashboard.
 */
export type DashboardAddress = { edit?: true; cf?: string } & { [K in FilterKey]?: unknown };

export function dashboardAddress(search: Record<string, unknown>): DashboardAddress {
  const address: DashboardAddress = {};
  // `?edit=true` reads as `true`; a hand-written `?edit="true"` as the string.
  if (search.edit === true || search.edit === 'true') address.edit = true;
  if (typeof search.cf === 'string') address.cf = search.cf;
  for (const key of FILTER_KEYS) if (search[key] !== undefined) address[key] = search[key];
  return address;
}
